'use client';

/**
 * StickyMobileCTA — Bottom-fixed conversion bar, mobile only (md:hidden).
 *
 * Design decisions:
 * - Reuses nav.cta.primary from content.ts via useLocale (same string as hero + nav).
 * - Amber fill + near-black label per WCAG rule (R6: sourced from the shared
 *   `LANDING_PRIMARY_CTA_RECIPE`, ui/cta-recipe.ts — single source of truth):
 *   amber-on-dark passes (~10:1); white-on-amber fails (1.9:1) — near-black fixes it.
 * - Reveals after user scrolls past one viewport height so it never covers the hero CTA.
 *   Trigger is an IntersectionObserver on the hero-end sentinel (data-hero-end).
 *   IO is compositor-native and passive by design — no main-thread scroll handler.
 * - 2026-07-10 fleet rebuild (Judge 2 forensic masterplan finding 7): the bar must
 *   ALSO yield to any section that already offers its own primary action — the bot
 *   test form, the pricing cards, the final CTA, and the footer's own links. A
 *   second IntersectionObserver watches `#bot`, `#pricing`, and every element
 *   carrying `[data-sticky-cta-suppress]` (FinalCTASection + FooterSection, both
 *   this lane's files); `visible = pastHero && !insideCompetingZone`.
 * - Entrance: compositor-only translateY + opacity, 300ms ease-out.
 *   prefers-reduced-motion: reduce → no transform, instant show (CSS media query).
 * - z-40: page-local, well below cosmos (10000) / dialog (10001) / dock z-50.
 * - env(safe-area-inset-bottom) padding clears iOS home indicator.
 * - prefetch={false} on Link: dynamic target, list-perf rule.
 * - R7 hallmark paper flip (2026-07-17, design contract L1.3): the bar surface
 *   is now PAPER_BAND.base + a top PAPER_RULE.hairline border, not the old
 *   dark bar — the CTA's own amber-fill/near-black-label recipe (above) is
 *   unaffected either way, since it never depended on the bar's own color.
 *
 * Related: [[knowledge/ui-components]] [[rules/glass-styling]] [[rules/ui-quality]]
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import { useLocale } from '../hooks/useLocale';
import { PAPER_BAND, PAPER_RULE } from '../ui/paper-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '../ui/cta-recipe';

/**
 * env() + pixel-fallback bottom padding (clears the iOS home indicator).
 * Hoisted to module scope (static) so it is not reallocated every render
 * (react-perf/jsx-no-new-object-as-prop).
 */
const STICKY_BAR_STYLE: React.CSSProperties = {
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
};

// Sections the sticky bar must yield to — each already carries its own primary
// action, so a persistent floating duplicate reads as noise (Judge 2 forensic
// masterplan finding 7). `#bot` and `#pricing` are the locked cross-lane anchor
// ids (shared handoff §"Anchors/ids"); the data attribute covers Final CTA and
// Footer, both added in this lane's own files.
const COMPETING_ZONE_SELECTORS = ['#bot', '#pricing', '[data-sticky-cta-suppress]'];

export default function StickyMobileCTA() {
  const { t } = useLocale();
  const { nav } = LANDING_CONTENT;

  // ── Reveal trigger: IO on the hero section bottom edge ────────────────────
  const [pastHero, setPastHero] = useState(false);
  // ── Competing-conversion-zone suppression (Judge 2 finding 7) ──────────────
  const [insideCompetingZone, setInsideCompetingZone] = useState(false);

  useEffect(() => {
    // Mobile-only: the bar is md:hidden — skip entirely on desktop.
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const cleanups: Array<() => void> = [];

    // Observe the [data-hero-end] sentinel that HeroSection renders at the very
    // bottom of the hero content. When that marker leaves the viewport, the hero
    // — including its CTA — is fully scrolled away, so revealing the sticky bar
    // can never cover the hero CTA. Fallback (sentinel somehow absent): a
    // one-shot scroll-position read so the bar is never permanently broken.
    const heroSentinel = document.querySelector('[data-hero-end]') as HTMLElement | null;

    if (heroSentinel) {
      const heroObserver = new IntersectionObserver(
        ([entry]) => {
          // CRITICAL: `!isIntersecting` is ambiguous — true both when the hero-end
          // marker sits BELOW the fold (fresh load at scrollY=0, hero CTA still on
          // screen) AND when it has scrolled ABOVE the viewport (user past the
          // hero). Using the boolean alone reveals the bar at scrollY=0, covering
          // the hero. Read the marker's POSITION: reveal ONLY when it is genuinely
          // above the viewport top (boundingClientRect.top < 0). At the top
          // (top > 0) → hidden; after scrolling past the hero (top < 0) → shown.
          setPastHero(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { root: null, rootMargin: '0px', threshold: 0 }
      );
      heroObserver.observe(heroSentinel);
      cleanups.push(() => heroObserver.disconnect());
    } else {
      // Fallback: one-shot scroll check, deferred to the next frame (rAF) so the
      // read is off the synchronous effect commit (avoids
      // react-hooks/set-state-in-effect) and reflects post-layout scroll geometry.
      const rafId = requestAnimationFrame(() => {
        setPastHero(window.scrollY > window.innerHeight * 0.8);
      });
      cleanups.push(() => cancelAnimationFrame(rafId));
    }

    // Competing-zone observer — tracks the live set of currently-intersecting
    // targets (a section can be tall enough that entry/exit events interleave).
    const zoneTargets = COMPETING_ZONE_SELECTORS.flatMap((selector) =>
      Array.from(document.querySelectorAll<HTMLElement>(selector))
    );
    if (zoneTargets.length > 0) {
      const intersecting = new Set<Element>();
      const zoneObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) intersecting.add(entry.target);
            else intersecting.delete(entry.target);
          }
          setInsideCompetingZone(intersecting.size > 0);
        },
        { root: null, rootMargin: '0px', threshold: 0 }
      );
      zoneTargets.forEach((el) => zoneObserver.observe(el));
      cleanups.push(() => zoneObserver.disconnect());
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  const visible = pastHero && !insideCompetingZone;

  const label = t(nav.cta.primary);

  return (
    <>
      {/*
        Reduced-motion styles injected via a <style> tag rather than Tailwind arbitrary
        variants to avoid needing a custom config entry. The motion-safe translate is
        handled via a data attribute so we never need JS to detect the media query.
      */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .sticky-mobile-cta {
            transform: none !important;
            transition: opacity 150ms ease-out !important;
          }
        }
      `}</style>

      <div
        // inert removes the whole subtree from focus order, AT, and pointer
        // when the bar is not visible — satisfies WCAG 2.4.3/2.4.7/4.1.2.
        // React 19 supports inert as a boolean prop natively.
        inert={!visible}
        aria-hidden={!visible}
        className={[
          'sticky-mobile-cta',
          'fixed bottom-0 left-0 right-0 z-40',
          'md:hidden',
          // R7 hallmark paper flip (design contract L1.3): solid paper surface
          // + top hairline, replacing the dark bar — no backdrop-filter, same
          // reasoning as before (the bar is always mounted, only opacity/
          // translateY toggled; a backdrop-blur would force a dedicated
          // compositor layer + GPU blur pass every frame all session, a
          // continuous tax on mid-range Android even when hidden).
          PAPER_BAND.base.bg,
          'border-t',
          PAPER_RULE.hairline.border,
          // Padding: 12px top, bottom = safe area + 12px for thumb comfort
          'px-4 pt-3',
          'transition-[transform,opacity] duration-300 ease-out',
          visible
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : 'translate-y-4 opacity-0 pointer-events-none',
        ].join(' ')}
        // pb composes env() with a pixel fallback Tailwind can't express; the
        // object is module-level (STICKY_BAR_STYLE) to keep a stable identity.
        style={STICKY_BAR_STYLE}
      >
        <Link
          href="/auth/register"
          prefetch={false}
          aria-label={label}
          // Belt-and-suspenders: inert on the container already removes focus
          // routing, but tabIndex=-1 adds an explicit opt-out for older UAs that
          // may not yet fully implement the inert attribute.
          tabIndex={visible ? undefined : -1}
          className={[
            // R2-7 (2026-07-10 review round) -> R6: primary CTA recipe now
            // sourced from cta-recipe.ts's LANDING_PRIMARY_CTA_RECIPE (single
            // source of truth for color/elevation/interaction/focus — this
            // recipe's own outline-based focus replaces the old ring classes).
            LANDING_PRIMARY_CTA_RECIPE,
            'flex items-center justify-center gap-2',
            'w-full min-h-[48px] rounded-xl font-semibold text-base',
          ].join(' ')}
        >
          <span>{label}</span>
          <ArrowRight className="w-4 h-4 shrink-0" aria-hidden="true" />
        </Link>
      </div>
    </>
  );
}
